import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { 
  History, 
  Edit3, 
  Trash2, 
  ArrowRight, 
  FileText, 
  Upload, 
  BarChart3,
  Shield,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Action icons and colors
const ACTION_CONFIG = {
  create: { icon: FileText, color: 'text-green-400', bgColor: 'bg-green-400/10' },
  update: { icon: Edit3, color: 'text-blue-400', bgColor: 'bg-blue-400/10' },
  delete: { icon: Trash2, color: 'text-red-400', bgColor: 'bg-red-400/10' },
  advance_phase: { icon: ArrowRight, color: 'text-purple-400', bgColor: 'bg-purple-400/10' },
  upload: { icon: Upload, color: 'text-yellow-400', bgColor: 'bg-yellow-400/10' },
  analysis: { icon: BarChart3, color: 'text-pink-400', bgColor: 'bg-pink-400/10' },
  permission: { icon: Shield, color: 'text-amber-400', bgColor: 'bg-amber-400/10' },
};

// Entity type labels
const ENTITY_LABELS = {
  project: 'Project',
  define_artifact: 'Define Artifact',
  improve_artifact: 'Improve Artifact',
  control_artifact: 'Control Artifact',
  dataset: 'Dataset',
  analysis: 'Analysis',
  fishbone: 'Fishbone',
  kpi: 'KPI',
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown time';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

const formatDetails = (action, entityType, details) => {
  if (!details) return null;
  
  const parts = [];
  
  if (action === 'invite' && details.email) {
    parts.push(`Invited ${details.email} as ${details.role?.replace('_', ' ')}`);
  } else if (action === 'remove' && details.name) {
    parts.push(`Removed ${details.name}`);
  } else if (action === 'update' && details.old_role && details.new_role) {
    parts.push(`Changed role: ${details.old_role} → ${details.new_role}`);
  } else if (action === 'advance_phase' && details.new_phase) {
    parts.push(`Advanced to ${details.new_phase.charAt(0).toUpperCase() + details.new_phase.slice(1)} phase`);
  } else if (details.members_count !== undefined) {
    parts.push(`${details.members_count} members updated`);
  } else if (typeof details === 'object') {
    // Generic key-value display for other details
    Object.entries(details).forEach(([key, value]) => {
      if (value && typeof value !== 'object') {
        parts.push(`${key.replace('_', ' ')}: ${value}`);
      }
    });
  }
  
  return parts.length > 0 ? parts.join(' | ') : null;
};

const AuditTrail = ({ projectId, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const token = localStorage.getItem('token');

  const fetchLogs = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/audit-log?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (loading) {
    return (
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="audit-trail-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-cyan-400" />
              Activity Log
            </CardTitle>
            <CardDescription className="text-slate-400">
              Track all changes and actions in this project
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchLogs(true)}
            disabled={refreshing}
            className="border-slate-600"
            data-testid="refresh-audit-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <div className="text-center py-12">
            <History className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No activity recorded yet</p>
            <p className="text-sm text-slate-500 mt-1">
              Actions like phase advances, data uploads, and updates will appear here
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {logs.map((log, index) => {
                const config = ACTION_CONFIG[log.action] || ACTION_CONFIG.update;
                const ActionIcon = config.icon;
                const entityLabel = ENTITY_LABELS[log.entity_type] || log.entity_type;
                const detailsText = formatDetails(log.action, log.entity_type, log.details);

                return (
                  <div
                    key={log.audit_id || index}
                    className="flex items-start gap-3 p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors"
                    data-testid={`audit-entry-${index}`}
                  >
                    <div className={`w-9 h-9 rounded-full ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <ActionIcon className={`w-4 h-4 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-medium text-sm">
                          {log.user_name || 'Unknown User'}
                        </span>
                        <Badge variant="outline" className={`text-xs ${config.color} border-current`}>
                          {log.action.replace('_', ' ')}
                        </Badge>
                        <Badge variant="secondary" className="text-xs bg-slate-600 text-slate-300">
                          {entityLabel}
                        </Badge>
                      </div>
                      {detailsText && (
                        <p className="text-sm text-slate-400 mt-1 truncate">
                          {detailsText}
                        </p>
                      )}
                      <p className="text-xs text-slate-500 mt-1">
                        {formatTimestamp(log.timestamp)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
        
        {logs.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-700 flex justify-between items-center">
            <p className="text-xs text-slate-500">
              Showing {logs.length} most recent activities
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AuditTrail;
